// The demo's front door.
//
// Sign-in happens BEFORE anything else: no anonymous session, no sandbox and no
// portal bundle until we know who the visitor is. The earlier design let people
// browse anonymously and asked afterwards — that produced 13 anonymous accounts
// and zero identified visitors.
//
// GOOGLE IS THE ONLY WAY IN. Email + password used to be offered as a fallback,
// behind an email-verification gate, and it earned its removal: of the four
// accounts it ever produced, the three real visitors all came through Google
// and the single password account was unverified junk ("asdasd@com.com").
// The fallback cost a form, a register/sign-in toggle, a verification screen
// and a resend flow — a lot of surface on the one screen that has to convert,
// in exchange for the only sign-ups we did not want. One tap, an address that
// is already verified, and a real name for the lead list.
//
// Note: removing the form does not by itself disable the Email/Password
// provider on the Firebase project. Turn that off in the console to close the
// path completely.

import { useState } from "react";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";

const C = {
  ink: "#16122b",
  muted: "#625d7a",
  violet: "#6248e0",
};

const shell = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  overflowY: "auto",
  fontFamily: "Inter, sans-serif",
  background:
    "radial-gradient(42rem 26rem at 78% -12%, rgba(255,130,87,.24), transparent 62%)," +
    "radial-gradient(40rem 26rem at 8% 108%, rgba(123,96,255,.46), transparent 62%)," +
    "linear-gradient(155deg,#120d24 0%,#1d1440 44%,#2f2069 100%)",
};

const card = {
  width: "100%",
  maxWidth: 430,
  borderRadius: 26,
  padding: "38px 30px 30px",
  background: "#fff",
  boxShadow: "0 30px 70px -20px rgba(23,18,46,.6)",
};

// Google's branding rules ask for their own light or dark button rather than a
// recoloured one, so this stays white with the standard mark even though it is
// now the only call to action on the card. The weight comes from size and
// shadow instead of colour.
const googleBtn = (busy) => ({
  width: "100%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  padding: "16px 22px",
  borderRadius: 999,
  border: "1px solid #ddd5fa",
  cursor: busy ? "default" : "pointer",
  opacity: busy ? 0.65 : 1,
  fontSize: 15.5,
  fontWeight: 700,
  color: C.ink,
  background: "#fff",
  boxShadow: "0 6px 18px -6px rgba(98,72,224,.35)",
});

function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 40.2 44 35 44 24c0-1.3-.1-2.6-.4-3.9z"/>
    </svg>
  );
}

const MESSAGES = {
  "auth/popup-blocked":
    "Your browser blocked the Google window. Allow pop-ups for this site and try again.",
  "auth/account-exists-with-different-credential":
    "That address is already registered here under a different sign-in method.",
  "auth/network-request-failed":
    "We couldn't reach Google. Check your connection and try again.",
  "auth/too-many-requests":
    "Too many attempts. Please wait a moment and try again.",
};

export default function DemoAuthScreen({ auth, onAuthenticated }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function withGoogle() {
    setBusy(true);
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const cred = await signInWithPopup(auth, provider);
      onAuthenticated(cred.user, true);
    } catch (err) {
      // Closing the Google window is a normal thing to do, not an error worth
      // showing — just release the button.
      if (
        err.code === "auth/popup-closed-by-user" ||
        err.code === "auth/cancelled-popup-request"
      ) {
        setBusy(false);
        return;
      }
      console.error("[demo] auth:", err);
      setError(MESSAGES[err.code] || "Something went wrong. Please try again.");
      setBusy(false);
    }
  }

  return (
    <div style={shell}>
      <div style={card}>
        <img
          src="/logo/logo192.png"
          alt=""
          width="54"
          height="54"
          style={{ borderRadius: 15, display: "block", margin: "0 auto 16px" }}
        />

        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 700,
            color: C.ink,
            textAlign: "center",
            letterSpacing: "-.02em",
          }}
        >
          Try the Staff Portal
        </h1>
        <p
          style={{
            margin: "11px 0 26px",
            fontSize: 14.5,
            lineHeight: 1.65,
            color: C.muted,
            textAlign: "center",
          }}
        >
          Sign in to open your own private copy of a hotel portal, with sample
          staff, news, training and chat already inside.
        </p>

        <button
          type="button"
          onClick={withGoogle}
          disabled={busy}
          style={googleBtn(busy)}
        >
          <GoogleMark />
          {busy ? "Opening Google…" : "Continue with Google"}
        </button>

        {error && (
          <p
            style={{
              margin: "16px 0 0",
              fontSize: 13.5,
              lineHeight: 1.55,
              color: "#c0392b",
              textAlign: "center",
            }}
            role="alert"
          >
            {error}
          </p>
        )}

        <p
          style={{
            margin: "22px 0 0",
            fontSize: 12,
            color: "#8b86a0",
            textAlign: "center",
            lineHeight: 1.55,
          }}
        >
          We only use your name and email to follow up about the portal.
        </p>
      </div>
    </div>
  );
}
